// Standalone Features panel for discovery mode.
//
// Reads from `discoveryFeatures` (server-side blob populated by the
// debounced /chat2 → Service Bus worker pipeline, or the manual
// "Re-extract" button which hits POST /api/sessions/{id}/discovery-features
// synchronously). Each feature shows the name + description the LLM
// produced over the deterministic candidate, plus chips for bounded
// context, touchpoints, statement ids, priority, and trigger.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import type { DiscoveryFeature, DiscoveryFeaturesPayload } from '../hooks/useSession';
import type { BoundedContextInfo } from '../utils/flowBoundedContext';
import '../styles/FeaturesPanel.css';

interface Props {
  payload: DiscoveryFeaturesPayload;
  isExtracting: boolean;
  hasFlow: boolean;
  currentStatementCount: number;
  onRefresh: () => void;
  onReExtract: () => void;
  onShowStatementsInFlow?: (statementIds: number[]) => void;
  // Shared BC filter — same selection drives the canvas. When set,
  // the panel splits into Primary (selected BC) and Related (BCs that
  // share a chain with it). When null, all features render in one list.
  selectedBoundedContext: string | null;
  relatedBoundedContexts: Set<string>;
  availableBoundedContexts: BoundedContextInfo[];
  onBoundedContextChange: (bc: string | null) => void;
}

type Filter = 'all' | 'mvp' | 'later';

const FeaturesPanel: React.FC<Props> = ({
  payload,
  isExtracting,
  hasFlow,
  currentStatementCount,
  onRefresh,
  onReExtract,
  onShowStatementsInFlow,
  selectedBoundedContext,
  relatedBoundedContexts,
  availableBoundedContexts,
  onBoundedContextChange,
}) => {
  const [filter, setFilter] = useState<Filter>('all');

  // Apply priority filter first (MVP / Later).
  const priorityFiltered = useMemo(() => {
    if (filter === 'all') return payload.features;
    return payload.features.filter((f) => f.priority === filter);
  }, [payload.features, filter]);

  // Then partition by BC role.
  const { primaryFeatures, relatedFeatures, otherFeatures } = useMemo(() => {
    if (!selectedBoundedContext) {
      return { primaryFeatures: priorityFiltered, relatedFeatures: [], otherFeatures: [] };
    }
    const primary: DiscoveryFeature[] = [];
    const related: DiscoveryFeature[] = [];
    const other: DiscoveryFeature[] = [];
    for (const f of priorityFiltered) {
      if (f.boundedContext === selectedBoundedContext) primary.push(f);
      else if (relatedBoundedContexts.has(f.boundedContext)) related.push(f);
      else other.push(f);
    }
    return { primaryFeatures: primary, relatedFeatures: related, otherFeatures: other };
  }, [priorityFiltered, selectedBoundedContext, relatedBoundedContexts]);

  const stale =
    payload.statementCountAtExtraction !== null &&
    payload.statementCountAtExtraction !== currentStatementCount;

  const counts = useMemo(() => {
    const out = { mvp: 0, later: 0 };
    for (const f of payload.features) out[f.priority] += 1;
    return out;
  }, [payload.features]);

  return (
    <div className="features-panel">
      <header className="features-header">
        <div className="features-title-block">
          <h2 className="features-title">Features</h2>
          <FeaturesStatus
            payload={payload}
            isExtracting={isExtracting}
            stale={stale}
            currentStatementCount={currentStatementCount}
          />
        </div>
        <div className="features-actions">
          <button
            type="button"
            className="features-btn features-btn--ghost"
            onClick={onRefresh}
            disabled={isExtracting}
            title="Re-fetch features from the server"
          >
            Refresh
          </button>
          <button
            type="button"
            className="features-btn features-btn--primary"
            onClick={onReExtract}
            disabled={isExtracting || !hasFlow}
            title={hasFlow
              ? 'Run extraction now without waiting for the debounced worker'
              : 'No flow yet — describe a process first'}
          >
            {isExtracting ? 'Extracting…' : 'Re-extract now'}
          </button>
        </div>
      </header>

      {(payload.features.length > 0 || availableBoundedContexts.length > 0) && (
        <div className="features-toolbar">
          <FilterTabs filter={filter} setFilter={setFilter} counts={counts} total={payload.features.length} />
          {availableBoundedContexts.length > 0 && (
            <BcDropdown
              allBcs={availableBoundedContexts}
              selected={selectedBoundedContext}
              onSelect={onBoundedContextChange}
            />
          )}
        </div>
      )}

      {payload.warnings.length > 0 && (
        <div className="features-warnings">
          <strong>{payload.warnings.length} warning{payload.warnings.length > 1 ? 's' : ''} from extraction</strong>
          <ul>
            {payload.warnings.slice(0, 5).map((w, i) => (
              <li key={i}>{w}</li>
            ))}
            {payload.warnings.length > 5 && (
              <li className="features-warnings-more">…+{payload.warnings.length - 5} more</li>
            )}
          </ul>
        </div>
      )}

      <div className="features-body">
        {!hasFlow ? (
          <EmptyState
            title="No flow yet"
            sub="Describe a process in the chat and features will appear here once the flow has at least one action."
          />
        ) : payload.features.length === 0 && !isExtracting ? (
          <EmptyState
            title="No features extracted yet"
            sub="Click Re-extract now, or wait for the debounced worker (~20s after each chat turn that changes the flow)."
          />
        ) : selectedBoundedContext ? (
          <>
            <FeatureSection
              title={`${selectedBoundedContext} (${primaryFeatures.length})`}
              subtitle="Features owned by this bounded context"
              features={primaryFeatures}
              onShowInFlow={onShowStatementsInFlow}
              variant="primary"
              emptyHint="No features owned by this bounded context match the current priority filter."
            />
            {relatedFeatures.length > 0 && (
              <FeatureSection
                title={`Related features (${relatedFeatures.length})`}
                subtitle="Features in bounded contexts that share a chain with the selected one"
                features={relatedFeatures}
                onShowInFlow={onShowStatementsInFlow}
                variant="related"
              />
            )}
            {otherFeatures.length > 0 && (
              <details className="features-other">
                <summary>Other features ({otherFeatures.length}) — not directly related</summary>
                <FeatureSection
                  title=""
                  features={otherFeatures}
                  onShowInFlow={onShowStatementsInFlow}
                  variant="other"
                />
              </details>
            )}
          </>
        ) : (
          <ul className="features-list">
            {primaryFeatures.map((f) => (
              <FeatureCard
                key={f.candidateId}
                feature={f}
                onShowInFlow={onShowStatementsInFlow}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
};

export default FeaturesPanel;

// ─── Sub-components ───────────────────────────────────────────────

function FeaturesStatus({
  payload,
  isExtracting,
  stale,
  currentStatementCount,
}: {
  payload: DiscoveryFeaturesPayload;
  isExtracting: boolean;
  stale: boolean;
  currentStatementCount: number;
}) {
  if (isExtracting || payload.status === 'pending') {
    return (
      <span className="features-status features-status--pending">
        <span className="features-status-dot" /> Extracting…
      </span>
    );
  }
  if (payload.status === 'error') {
    return (
      <span className="features-status features-status--error">Last extraction failed</span>
    );
  }
  if (!payload.extractedAt) {
    return <span className="features-status features-status--idle">Not yet extracted</span>;
  }
  if (stale) {
    return (
      <span className="features-status features-status--stale">
        Stale — {payload.statementCountAtExtraction}/{currentStatementCount} statements covered
      </span>
    );
  }
  return (
    <span className="features-status features-status--idle">
      Up to date · {payload.features.length} feature{payload.features.length !== 1 ? 's' : ''} · extracted {shortAgo(payload.extractedAt)}
    </span>
  );
}

function FilterTabs({
  filter,
  setFilter,
  counts,
  total,
}: {
  filter: Filter;
  setFilter: (f: Filter) => void;
  counts: { mvp: number; later: number };
  total: number;
}) {
  return (
    <div className="features-filter-tabs" role="tablist">
      <button
        type="button"
        role="tab"
        aria-selected={filter === 'all'}
        className={`features-filter-tab ${filter === 'all' ? 'features-filter-tab--active' : ''}`}
        onClick={() => setFilter('all')}
      >
        All <span className="features-filter-count">{total}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={filter === 'mvp'}
        className={`features-filter-tab ${filter === 'mvp' ? 'features-filter-tab--active' : ''}`}
        onClick={() => setFilter('mvp')}
      >
        MVP <span className="features-filter-count">{counts.mvp}</span>
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={filter === 'later'}
        className={`features-filter-tab ${filter === 'later' ? 'features-filter-tab--active' : ''}`}
        onClick={() => setFilter('later')}
      >
        Later <span className="features-filter-count">{counts.later}</span>
      </button>
    </div>
  );
}

function BcDropdown({
  allBcs,
  selected,
  onSelect,
}: {
  allBcs: BoundedContextInfo[];
  selected: string | null;
  onSelect: (bc: string | null) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const wrapRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDoc);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => { if (open) inputRef.current?.focus(); }, [open]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allBcs;
    return allBcs.filter((b) => b.name.toLowerCase().includes(q));
  }, [allBcs, query]);

  return (
    <div className="features-bc-wrap" ref={wrapRef}>
      <button
        type="button"
        className={`features-bc-trigger ${selected ? 'features-bc-trigger--active' : ''}`}
        onClick={() => setOpen((o) => !o)}
        title="Filter features by bounded context"
      >
        <span className="features-bc-label">Bounded context</span>
        <span className="features-bc-current">{selected ?? `All (${allBcs.length})`}</span>
        <span className="features-bc-chev">▾</span>
      </button>
      {open && (
        <div className="features-bc-menu" role="listbox">
          <input
            ref={inputRef}
            type="text"
            className="features-bc-search"
            placeholder="Search bounded contexts…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          <button
            type="button"
            className={`features-bc-item ${selected === null ? 'features-bc-item--active' : ''}`}
            onClick={() => { onSelect(null); setOpen(false); }}
          >
            <span className="features-bc-item-name">All bounded contexts</span>
            <span className="features-bc-item-count">{allBcs.length}</span>
          </button>
          <div className="features-bc-divider" />
          <div className="features-bc-list">
            {filtered.length === 0 && <div className="features-bc-empty">No matches</div>}
            {filtered.map((b) => (
              <button
                key={b.name}
                type="button"
                className={`features-bc-item ${selected === b.name ? 'features-bc-item--active' : ''}`}
                onClick={() => { onSelect(b.name); setOpen(false); }}
              >
                <span className="features-bc-item-name">{b.name}</span>
                <span className="features-bc-item-count">{b.statementCount}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FeatureSection({
  title,
  subtitle,
  features,
  onShowInFlow,
  variant,
  emptyHint,
}: {
  title: string;
  subtitle?: string;
  features: DiscoveryFeature[];
  onShowInFlow?: (statementIds: number[]) => void;
  variant: 'primary' | 'related' | 'other';
  emptyHint?: string;
}) {
  return (
    <section className={`features-section features-section--${variant}`}>
      {title && (
        <header className="features-section-head">
          <h3 className="features-section-title">{title}</h3>
          {subtitle && <p className="features-section-sub">{subtitle}</p>}
        </header>
      )}
      {features.length === 0 ? (
        emptyHint ? <div className="features-section-empty">{emptyHint}</div> : null
      ) : (
        <ul className="features-list">
          {features.map((f) => (
            <FeatureCard
              key={f.candidateId}
              feature={f}
              onShowInFlow={onShowInFlow}
            />
          ))}
        </ul>
      )}
    </section>
  );
}

function FeatureCard({
  feature,
  onShowInFlow,
}: {
  feature: DiscoveryFeature;
  onShowInFlow?: (statementIds: number[]) => void;
}) {
  const [expanded, setExpanded] = useState(true);
  const tagsList = feature.tags ? Object.entries(feature.tags) : [];
  const initiating = feature.initiatingActor;
  const inputsVia = feature.inputs?.via ?? [];
  const inputsTarget = feature.inputs?.target ?? null;
  const outputs = feature.outputs ?? [];
  const preconditions = feature.preconditions ?? [];
  const acceptanceCriteria = feature.acceptanceCriteria ?? [];
  const openQuestions = feature.openQuestions ?? [];
  const hasDetails = !!(
    feature.userStory
    || acceptanceCriteria.length
    || openQuestions.length
    || preconditions.length
    || outputs.length
    || feature.chainSummary
  );

  return (
    <li className={`features-card features-card--${feature.priority}`}>
      <div className="features-card-head">
        <span className={`features-trigger-badge features-trigger-badge--${feature.trigger}`}>
          {triggerLabel(feature.trigger)}
        </span>
        <span className={`features-priority-badge features-priority-badge--${feature.priority}`}>
          {feature.priority.toUpperCase()}
        </span>
        {tagsList.map(([k, v]) => (
          <span key={k} className="features-meta-chip features-meta-chip--tag" title={`Tag #${k}${v === true ? '' : `=${v}`}`}>
            #{k}{v === true ? '' : `=${v}`}
          </span>
        ))}
        {hasDetails && (
          <button
            type="button"
            className="features-card-expand"
            onClick={() => setExpanded((v) => !v)}
            aria-expanded={expanded}
            title={expanded ? 'Hide details' : 'Show details'}
          >
            {expanded ? '▾ Hide details' : '▸ Show details'}
          </button>
        )}
      </div>
      <h3 className="features-card-name">{feature.name}</h3>
      <p className="features-card-desc">{feature.description}</p>

      <div className="features-card-meta">
        {feature.boundedContext && (
          <span className="features-meta-chip features-meta-chip--bc" title="Bounded context">
            ◆ {feature.boundedContext}
          </span>
        )}
        {initiating && (
          <span className="features-meta-chip features-meta-chip--actor" title="Initiating actor">
            {actorSigil(initiating.kind)}{initiating.name}
          </span>
        )}
        {feature.touchpoints.map((tp) => (
          <span key={tp} className="features-meta-chip features-meta-chip--tp" title="Touchpoint">
            🖥 {tp}
          </span>
        ))}
        {feature.targets.map((t) => (
          <span key={t} className="features-meta-chip features-meta-chip--target" title="Aggregate / target">
            &amp;{t}
          </span>
        ))}
      </div>

      {expanded && hasDetails && (
        <div className="features-card-details">
          {feature.userStory && (
            <div className="features-detail-block features-detail-block--story">
              <div className="features-detail-label">User story</div>
              <p className="features-detail-story">{feature.userStory}</p>
            </div>
          )}

          {(preconditions.length > 0 || outputs.length > 0 || inputsVia.length > 0 || inputsTarget) && (
            <div className="features-detail-block">
              <div className="features-detail-grid">
                {preconditions.length > 0 && (
                  <div>
                    <div className="features-detail-label">Triggered by</div>
                    <ul className="features-detail-chips">
                      {preconditions.map((p) => (
                        <li key={p.id} className="features-detail-chip features-detail-chip--event">
                          ◆ {p.name}
                          {p.target && <span className="features-detail-chip-sub">&amp;{p.target}</span>}
                          <span className="features-detail-chip-id">[{p.id}]</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {(inputsVia.length > 0 || inputsTarget) && (
                  <div>
                    <div className="features-detail-label">Inputs</div>
                    <ul className="features-detail-chips">
                      {inputsTarget && (
                        <li className="features-detail-chip features-detail-chip--target">
                          &amp;{inputsTarget}
                        </li>
                      )}
                      {inputsVia.map((v) => (
                        <li key={v} className="features-detail-chip features-detail-chip--via">
                          🖥 {v}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                {outputs.length > 0 && (
                  <div>
                    <div className="features-detail-label">Outputs</div>
                    <ul className="features-detail-chips">
                      {outputs.map((o) => (
                        <li key={o.id} className="features-detail-chip features-detail-chip--event">
                          ◆ {o.name}
                          {o.target && <span className="features-detail-chip-sub">&amp;{o.target}</span>}
                          <span className="features-detail-chip-id">[{o.id}]</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            </div>
          )}

          {acceptanceCriteria.length > 0 && (
            <div className="features-detail-block">
              <div className="features-detail-label">Acceptance criteria</div>
              <ul className="features-detail-bullets">
                {acceptanceCriteria.map((b, i) => (
                  <li key={i}>
                    <BulletText text={b.text} onShowInFlow={onShowInFlow} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {openQuestions.length > 0 && (
            <div className="features-detail-block features-detail-block--questions">
              <div className="features-detail-label">Open questions</div>
              <ul className="features-detail-bullets">
                {openQuestions.map((b, i) => (
                  <li key={i}>
                    <BulletText text={b.text} onShowInFlow={onShowInFlow} />
                  </li>
                ))}
              </ul>
            </div>
          )}

          {feature.chainSummary && (
            <div className="features-detail-block">
              <div className="features-detail-label">Chain</div>
              <code className="features-detail-chain">{feature.chainSummary}</code>
            </div>
          )}
        </div>
      )}

      <div className="features-card-footer">
        <span className="features-statements-label">Statements:</span>
        {feature.statementIds.map((id) => (
          <span key={id} className="features-statement-chip">
            [{id}]
          </span>
        ))}
        {onShowInFlow && (
          <button
            type="button"
            className="features-show-in-flow"
            onClick={() => onShowInFlow(feature.statementIds)}
            title="Highlight these statements on the flow canvas"
          >
            show in flow →
          </button>
        )}
      </div>
    </li>
  );
}

// Render a bullet making each [n] anchor clickable — clicking jumps the
// flow canvas to that single statement. Falls back to plain text when
// onShowInFlow isn't provided.
function BulletText({
  text,
  onShowInFlow,
}: {
  text: string;
  onShowInFlow?: (statementIds: number[]) => void;
}) {
  if (!onShowInFlow) return <>{text}</>;
  // Split on [n] occurrences, interleaving text and clickable spans.
  const parts: React.ReactNode[] = [];
  const regex = /\[(\d+)\]/g;
  let last = 0;
  let match;
  let key = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    const id = parseInt(match[1], 10);
    parts.push(
      <button
        key={`a-${key++}`}
        type="button"
        className="features-anchor"
        onClick={() => onShowInFlow([id])}
        title={`Show statement [${id}] on the canvas`}
      >
        [{id}]
      </button>,
    );
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return <>{parts}</>;
}

function actorSigil(kind: 'human' | 'context' | 'externalSystem'): string {
  if (kind === 'human') return '@!';
  if (kind === 'externalSystem') return '@^';
  return '@';
}

function EmptyState({ title, sub }: { title: string; sub: string }) {
  return (
    <div className="features-empty">
      <div className="features-empty-icon">◇</div>
      <div className="features-empty-title">{title}</div>
      <div className="features-empty-sub">{sub}</div>
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────

function triggerLabel(t: DiscoveryFeature['trigger']): string {
  if (t === 'user-action') return '👤 USER ACTION';
  if (t === 'system-reaction') return '⚡ SYSTEM REACTION';
  if (t === 'external-system') return '🔌 EXTERNAL';
  return t;
}

function shortAgo(iso: string): string {
  try {
    const ms = Date.now() - new Date(iso).getTime();
    if (ms < 60_000) return 'just now';
    if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
    if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
    return new Date(iso).toLocaleDateString();
  } catch {
    return iso;
  }
}
